import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiRequest } from "../../../redux/api";
import { buildAppLink } from "../../../utils/landingRedirect";

interface StripePrice {
  id: string;
  amount: number | null;
  currency: string;
  interval?: string;
  intervalCount?: number;
}

type PaidPlanId = "plus" | "pro" | "max";
type PlanId = "free" | PaidPlanId;
type BillingInterval = "monthly" | "yearly";

type TierPrices = Record<PaidPlanId, Record<BillingInterval, StripePrice | null>>;

type DisplayPrice = {
  currency: string;
  monthlyAmount: string;
  yearlyAmount: string;
};

const EMPTY_TIER_PRICES: TierPrices = {
  plus: { monthly: null, yearly: null },
  pro: { monthly: null, yearly: null },
  max: { monthly: null, yearly: null },
};

const CHINA_PRICE_AMOUNTS: Record<PaidPlanId, Record<BillingInterval, number>> = {
  plus: { monthly: 3500, yearly: 30000 },
  pro: { monthly: 6800, yearly: 68800 },
  max: { monthly: 20000, yearly: 204800 },
};

const INTERNATIONAL_FALLBACK_PRICE_AMOUNTS: Record<
  string,
  Record<PaidPlanId, Record<BillingInterval, number>>
> = {
  USD: {
    plus: { monthly: 1999, yearly: 19999 },
    pro: { monthly: 4999, yearly: 49999 },
    max: { monthly: 9999, yearly: 99999 },
  },
  CAD: {
    plus: { monthly: 2499, yearly: 24999 },
    pro: { monthly: 5999, yearly: 59999 },
    max: { monthly: 11999, yearly: 119999 },
  },
};

const getForwardedPricingQuery = () => {
  if (typeof window === "undefined") return "";

  const current = new URLSearchParams(window.location.search);
  const forwarded = new URLSearchParams();
  const currency = current.get("currency");
  const forceCny = current.get("forceCny");

  if (currency) forwarded.set("currency", currency);
  if (forceCny === "1") forwarded.set("forceCny", "1");

  const query = forwarded.toString();
  return query ? `?${query}` : "";
};

const getForcedCurrency = () => {
  if (typeof window === "undefined") return null;

  const current = new URLSearchParams(window.location.search);
  if (current.get("forceCny") === "1") return "CNY";

  return current.get("currency")?.toUpperCase() || null;
};

const formatAmount = (cents: number) => {
  const amount = cents / 100;
  const hasCents = Math.round(cents) % 100 !== 0;

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  }).format(amount);
};

const currencySymbol = (currency?: string) => {
  const code = currency?.toUpperCase();
  if (code === "CNY" || code === "JPY") return "¥";
  if (code === "CAD") return "C$";
  if (code === "EUR") return "€";
  if (code === "GBP") return "£";
  return "$";
};

const getPlanFallbackCurrency = (currency?: string) => {
  const normalized = currency?.toUpperCase() || "USD";
  return INTERNATIONAL_FALLBACK_PRICE_AMOUNTS[normalized] ? normalized : "USD";
};

const getPricingLink = () => buildAppLink(`/pricing${getForwardedPricingQuery()}`);

const PricingSection: React.FC = () => {
  const { t } = useTranslation();
  const [stripePrices, setStripePrices] = useState<TierPrices>(EMPTY_TIER_PRICES);
  const [marketCurrency, setMarketCurrency] = useState<string>("USD");
  const [priceCurrency, setPriceCurrency] = useState<string>("USD");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setIsLoading(true);

        const forcedCurrency = getForcedCurrency();
        const pricingQuery = getForwardedPricingQuery();
        const [marketResult, pricesResult] = await Promise.allSettled([
          forcedCurrency
            ? Promise.resolve({ currency: forcedCurrency })
            : apiRequest({
                url: "subscriptions/detect-currency",
                method: "GET",
                useAuthHeader: false,
              }),
          apiRequest({
            url: `subscriptions/stripe-prices-public${pricingQuery}`,
            method: "GET",
            useAuthHeader: false,
          }),
        ]);

        if (cancelled) return;

        if (marketResult.status === "fulfilled") {
          const detectedCurrency = marketResult.value?.currency?.toUpperCase();
          if (detectedCurrency) {
            setMarketCurrency(detectedCurrency);
          }
        }

        if (pricesResult.status === "fulfilled") {
          const res = pricesResult.value;
          if (res.currency) {
            setPriceCurrency(res.currency.toUpperCase());
          }
          if (res.tiers) {
            setStripePrices({
              plus: {
                monthly: res.tiers.plus?.monthly || null,
                yearly: res.tiers.plus?.yearly || null,
              },
              pro: {
                monthly: res.tiers.pro?.monthly || null,
                yearly: res.tiers.pro?.yearly || null,
              },
              max: {
                monthly: res.tiers.max?.monthly || null,
                yearly: res.tiers.max?.yearly || null,
              },
            });
          }
        }
      } catch {
        // Keep local fallback prices.
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const isChinaPricing = marketCurrency === "CNY";
  const displayCurrency = isChinaPricing ? "CNY" : priceCurrency;
  const fallbackCurrency = getPlanFallbackCurrency(displayCurrency);

  const getDisplayPrice = (tier: PaidPlanId): DisplayPrice => {
    if (isChinaPricing) {
      return {
        currency: "CNY",
        monthlyAmount: formatAmount(CHINA_PRICE_AMOUNTS[tier].monthly),
        yearlyAmount: formatAmount(CHINA_PRICE_AMOUNTS[tier].yearly),
      };
    }

    const monthly = stripePrices[tier].monthly;
    const yearly = stripePrices[tier].yearly;
    const fallback = INTERNATIONAL_FALLBACK_PRICE_AMOUNTS[fallbackCurrency][tier];
    const currency =
      monthly?.currency?.toUpperCase() ||
      yearly?.currency?.toUpperCase() ||
      fallbackCurrency;

    return {
      currency,
      monthlyAmount: formatAmount(monthly?.amount ?? fallback.monthly),
      yearlyAmount: formatAmount(yearly?.amount ?? fallback.yearly),
    };
  };

  const plans = [
    {
      id: "free" as PlanId,
      name: t("modern.pricing.free.name", "Free"),
      subtitle: t("modern.pricing.free.subtitle", "For individuals getting started"),
      buttonText: t("modern.pricing.free.button", "Start for free"),
      buttonLink: buildAppLink(),
      price: null,
      features: [
        t("modern.pricing.free.f1", "500 daily refresh credits"),
        t("modern.pricing.free.f2", "Import PDFs, YouTube videos, and images"),
        t("modern.pricing.free.f3", "Daily refresh credits for quick tasks"),
        t("modern.pricing.free.f4", "Access to AI tools and folder chat"),
        t("modern.pricing.free.f5", "Export to PDF and DOCX"),
        t("modern.pricing.free.f6", "Cross-reference across all your content"),
      ],
    },
    {
      id: "plus" as PlanId,
      name: t("modern.pricing.plus.name", "Plus"),
      subtitle: t("modern.pricing.plus.subtitle", "Everyday capture and generation"),
      buttonText: t("modern.pricing.plus.button", "Start free trial"),
      buttonLink: getPricingLink(),
      price: getDisplayPrice("plus"),
      features: [
        t("modern.pricing.plus.f1", "21,000 monthly AI credits"),
        t("modern.pricing.plus.f2", "More PDF, YouTube, image, and audio capture"),
        t("modern.pricing.plus.f3", "Daily refresh credits for everyday work"),
        t("modern.pricing.plus.f4", "Study tools, folder chat, and workflow drafts"),
        t("modern.pricing.plus.f5", "Export to PDF, DOCX, Markdown"),
        t("modern.pricing.plus.f6", "Cross-reference across all materials"),
      ],
    },
    {
      id: "pro" as PlanId,
      name: t("modern.pricing.pro.name", "Pro"),
      subtitle: t("modern.pricing.pro.subtitle", "Advanced models for serious work"),
      buttonText: t("modern.pricing.pro.button", "Start free trial"),
      buttonLink: getPricingLink(),
      price: getDisplayPrice("pro"),
      popular: true,
      features: [
        t("modern.pricing.pro.f1", "55,000 monthly AI credits"),
        t("modern.pricing.pro.f2", "Advanced AI models including premium research models"),
        t("modern.pricing.pro.f3", "Higher recording, import, and workflow limits"),
        t("modern.pricing.pro.f4", "PDF graph/table/formula recognition"),
        t("modern.pricing.pro.f5", "Large PDF auto-split into notes"),
        t("modern.pricing.pro.f6", "Priority support"),
      ],
    },
    {
      id: "max" as PlanId,
      name: t("modern.pricing.max.name", "Max"),
      subtitle: t("modern.pricing.max.subtitle", "High-volume agentic automation"),
      buttonText: t("modern.pricing.max.button", "Start free trial"),
      buttonLink: getPricingLink(),
      price: getDisplayPrice("max"),
      features: [
        t("modern.pricing.max.f1", "1,200,000 monthly AI credits"),
        t("modern.pricing.max.f2", "Highest agent, workflow, and context limits"),
        t("modern.pricing.max.f3", "All advanced and premium model access"),
        t("modern.pricing.max.f4", "AI Agent access"),
        t("modern.pricing.max.f5", "Highest recording and import limits"),
        t("modern.pricing.max.f6", "Priority support"),
      ],
    },
  ];

  return (
    <section
      className="scroll-mt-20 bg-gray-50 py-8 md:flex md:min-h-[calc(100vh-74px)] md:items-center md:py-0"
      id="pricing"
    >
      <div className="mx-auto w-full max-w-[1320px] px-5 lg:px-7">
        <motion.div
          className="mb-6 text-center"
          initial={{ opacity: 0, y: 20 }}
          viewport={{ once: true }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <h2 className="mb-2 text-2xl font-bold text-gray-900 md:text-[30px]">
            {t("modern.pricing.title", "Choose Your Plan")}
          </h2>
          <p className="text-sm text-gray-500 md:text-[15px]">
            {t("modern.pricing.subtitle", "Credits for capture, source-grounded answers, memory, and workflows")}
          </p>
        </motion.div>

        <div className="mx-auto grid max-w-[1260px] grid-cols-1 items-stretch gap-4 md:grid-cols-2 lg:grid-cols-4">
          {plans.map((plan, i) => (
            <motion.div
              className={`relative flex flex-col rounded-xl bg-white ${
                plan.popular
                  ? "border border-[#4ea4f5] shadow-[0_12px_30px_rgba(78,164,245,0.14)]"
                  : "border border-gray-200 shadow-[0_10px_26px_rgba(17,24,39,0.07)]"
              }`}
              initial={{ opacity: 0, y: 30 }}
              key={plan.id}
              transition={{ delay: i * 0.08 }}
              viewport={{ once: true }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              {plan.popular && (
                <div className="absolute -top-2 left-4">
                  <span className="rounded-md bg-[#eaf5ff] px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#226fb7]">
                    {t("modern.pricing.popular", "Most Popular")}
                  </span>
                </div>
              )}

              <div className="flex flex-1 flex-col p-3.5 xl:p-4">
                <h3 className="mt-0 text-lg font-bold text-gray-900">{plan.name}</h3>
                <p className="mb-3 mt-1 min-h-[30px] text-[12px] leading-4 text-gray-500">
                  {plan.subtitle}
                </p>

                <div className="mb-3 min-h-[58px]">
                  {plan.price ? (
                    <>
                      <div className="flex items-start">
                        <span className="mr-0.5 mt-0.5 text-sm font-bold text-gray-900">
                          {currencySymbol(plan.price.currency)}
                        </span>
                        <span className="text-[29px] font-bold leading-none text-gray-900">
                          {isLoading ? "···" : plan.price.monthlyAmount}
                        </span>
                        <div className="ml-1.5 mt-1.5">
                          <span className="block text-[10px] font-bold text-gray-400">
                            {plan.price.currency}
                          </span>
                          <span className="text-[10px] font-medium text-gray-400">
                            {t("modern.pricing.monthly", "/ month")}
                          </span>
                        </div>
                      </div>
                      <div className="mt-1 text-[12px] font-semibold text-gray-500">
                        {currencySymbol(plan.price.currency)}
                        {isLoading ? "···" : plan.price.yearlyAmount}
                        <span className="font-medium"> {t("modern.pricing.yearly", "/ year")}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-[29px] font-bold leading-none text-green-500">
                        {t("modern.pricing.free.price", "Free")}
                      </div>
                      <div className="mt-1.5 text-[12px] font-semibold text-gray-500">
                        {t("modern.pricing.free.yearly", "Always free")}
                      </div>
                    </>
                  )}
                </div>

                <a
                  className={`mb-3 flex h-9 w-full items-center justify-center rounded-[10px] px-3.5 text-center text-[12px] font-semibold no-underline transition-colors ${
                    plan.popular
                      ? "bg-[#4ea4f5] text-white hover:bg-[#226fb7]"
                      : "bg-gray-900 text-white hover:bg-gray-800"
                  }`}
                  href={plan.buttonLink}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {plan.buttonText}
                </a>

                <div className="mb-3 border-t border-gray-100" />

                <ul className="flex-1 space-y-1.5">
                  {plan.features.map((feature, index) => (
                    <li className="flex items-start gap-1.5" key={feature}>
                      <Icon
                        className="mt-[3px] flex-shrink-0 text-green-500"
                        icon="lucide:check"
                        width={12}
                      />
                      <span
                        className={`text-[11px] leading-snug text-gray-600 xl:text-[11.5px] ${
                          index === 0 && plan.id !== "free" ? "font-semibold" : ""
                        }`}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
